/** Marqueurs cliniques par dent (fiche IPR / stripping). */

export const ALL_TEETH = [
  18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
  48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
];

export const UPPER_TEETH = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const LOWER_TEETH = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

function buildContacts(teeth, arch) {
  return teeth.slice(0, -1).map((left, index) => {
    const right = teeth[index + 1];
    return {
      id: `${left}-${right}`,
      label: `${left}–${right}`,
      left,
      right,
      arch,
    };
  });
}

export const UPPER_CONTACTS = buildContacts(UPPER_TEETH, 'upper');
export const LOWER_CONTACTS = buildContacts(LOWER_TEETH, 'lower');
export const ALL_CONTACTS = [...UPPER_CONTACTS, ...LOWER_CONTACTS];

/** Mode sélection IPR (pas un marqueur). */
export const IPR_TOOL_SELECT = 'select';

/**
 * Quatre critères cliniques — codes courts lisibles sur le schéma (pas d’emoji).
 * Couleurs distinctes + lettre pour accessibilité (daltonisme).
 */
export const TOOTH_MARKERS = [
  {
    key: 'missing',
    label: 'Dent manquante',
    shortLabel: 'M',
    description: 'Dent absente — IPR désactivé sur cette dent',
    swatchClass: 'bg-slate-500',
    badgeClass: 'bg-slate-600 text-white',
    toothFill: 'rgb(226 232 240)',
    toothStroke: 'rgb(100 116 139)',
  },
  {
    key: 'bouton',
    label: 'Bouton',
    shortLabel: 'B',
    description: 'Bouton collé sur la dent',
    swatchClass: 'bg-amber-500',
    badgeClass: 'bg-amber-500 text-white',
    toothFill: null,
    toothStroke: null,
  },
  {
    key: 'elasticHooks',
    label: 'Crochets élastiques',
    shortLabel: 'E',
    description: 'Crochets pour élastiques',
    swatchClass: 'bg-violet-500',
    badgeClass: 'bg-violet-600 text-white',
    toothFill: null,
    toothStroke: null,
  },
  {
    key: 'attachments',
    label: 'Attachements',
    shortLabel: 'A',
    description: 'Attachements composite',
    swatchClass: 'bg-sky-500',
    badgeClass: 'bg-sky-600 text-white',
    toothFill: null,
    toothStroke: null,
  },
];

export const TOOTH_MARKER_KEYS = TOOTH_MARKERS.map((m) => m.key);

export function getToothMarker(key) {
  return TOOTH_MARKERS.find((m) => m.key === key) || null;
}

export function emptyToothRecord() {
  return {
    mesial: 0,
    distal: 0,
    missing: false,
    bouton: false,
    elasticHooks: false,
    attachments: false,
  };
}

export function buildEmptyIpr() {
  return Object.fromEntries(ALL_TEETH.map((t) => [t, emptyToothRecord()]));
}

/** Fusionne une fiche persistée avec les champs marqueurs par défaut. */
export function normalizeIprData(raw) {
  const base = buildEmptyIpr();
  if (!raw || typeof raw !== 'object') return base;

  for (const tooth of ALL_TEETH) {
    const src = raw[tooth] ?? raw[String(tooth)];
    if (!src || typeof src !== 'object') continue;
    const missing = Boolean(src.missing);
    base[tooth] = {
      mesial: missing ? 0 : Number(src.mesial) || 0,
      distal: missing ? 0 : Number(src.distal) || 0,
      missing,
      bouton: missing ? false : Boolean(src.bouton),
      elasticHooks: missing ? false : Boolean(src.elasticHooks),
      attachments: missing ? false : Boolean(src.attachments),
    };
  }
  return base;
}

function resolveContact(contactOrId) {
  if (contactOrId && typeof contactOrId === 'object') return contactOrId;
  return ALL_CONTACTS.find((contact) => contact.id === String(contactOrId)) || null;
}

export function getCenterContactForTooth(toothNumber) {
  const tooth = Number(toothNumber);
  const teeth = UPPER_TEETH.includes(tooth) ? UPPER_TEETH : LOWER_TEETH.includes(tooth) ? LOWER_TEETH : null;
  if (!teeth) return null;

  const index = teeth.indexOf(tooth);
  const contactIndex = index <= 7 ? index : index - 1;
  const contacts = teeth === UPPER_TEETH ? UPPER_CONTACTS : LOWER_CONTACTS;
  return contacts[contactIndex] || null;
}

export function getSelectionToothForContact(contactOrId) {
  const contact = resolveContact(contactOrId);
  if (!contact) return null;
  const contacts = contact.arch === 'upper' ? UPPER_CONTACTS : LOWER_CONTACTS;
  const index = contacts.findIndex((item) => item.id === contact.id);
  return index <= 7 ? contact.left : contact.right;
}

export function getIprContactValue(iprData, contactOrId) {
  const contact = resolveContact(contactOrId);
  if (!contact) return 0;
  const left = iprData?.[contact.left];
  const right = iprData?.[contact.right];
  if (left?.missing || right?.missing) return 0;
  const value = (Number(left?.distal) || 0) + (Number(right?.mesial) || 0);
  return Math.round(value * 100) / 100;
}

export function isIprContactDisabled(iprData, contactOrId) {
  const contact = resolveContact(contactOrId);
  if (!contact) return true;
  return Boolean(iprData?.[contact.left]?.missing || iprData?.[contact.right]?.missing);
}

export function updateIprContact(iprData, contactOrId, value) {
  const contact = resolveContact(contactOrId);
  if (!contact || isIprContactDisabled(iprData, contact)) return iprData;

  const parsed = Number.parseFloat(value);
  const normalized = Number.isFinite(parsed)
    ? Math.min(1, Math.max(0, Math.round(parsed * 100) / 100))
    : 0;
  const left = { ...emptyToothRecord(), ...(iprData?.[contact.left] || {}) };
  const right = { ...emptyToothRecord(), ...(iprData?.[contact.right] || {}) };

  return {
    ...iprData,
    [contact.left]: { ...left, distal: normalized },
    [contact.right]: { ...right, mesial: 0 },
  };
}

export function computeIprTotal(iprData) {
  const total = ALL_CONTACTS.reduce(
    (sum, contact) => sum + getIprContactValue(iprData, contact),
    0
  );
  return total.toFixed(2);
}

export function countContactsWithIpr(iprData) {
  return ALL_CONTACTS.filter(
    (contact) => getIprContactValue(iprData, contact) > 0
  ).length;
}

export function countTeethWithIpr(iprData) {
  return Object.values(iprData || {}).filter(
    (t) => !t?.missing && ((t?.mesial || 0) > 0 || (t?.distal || 0) > 0),
  ).length;
}

export function getActiveMarkers(tooth) {
  if (!tooth) return [];
  return TOOTH_MARKERS.filter((m) => Boolean(tooth[m.key]));
}

/**
 * Bascule un marqueur. « missing » exclut les autres marques et remet l’IPR à 0.
 */
export function toggleToothMarker(iprData, toothNumber, markerKey) {
  const current = { ...emptyToothRecord(), ...(iprData?.[toothNumber] || {}) };
  const nextValue = !current[markerKey];

  let next = { ...current, [markerKey]: nextValue };

  if (markerKey === 'missing') {
    if (nextValue) {
      next = {
        ...emptyToothRecord(),
        missing: true,
      };
    }
  } else if (next.missing) {
    // Impossible d’ajouter un appareil sur une dent manquante
    return iprData;
  }

  let updated = {
    ...iprData,
    [toothNumber]: next,
  };

  if (markerKey === 'missing' && nextValue) {
    ALL_CONTACTS.filter(
      (contact) => contact.left === Number(toothNumber) || contact.right === Number(toothNumber)
    ).forEach((contact) => {
      updated = {
        ...updated,
        [contact.left]: {
          ...emptyToothRecord(),
          ...(updated[contact.left] || {}),
          distal: 0,
        },
        [contact.right]: {
          ...emptyToothRecord(),
          ...(updated[contact.right] || {}),
          mesial: 0,
        },
      };
    });
  }

  return updated;
}

export function updateToothIprValue(iprData, toothNumber, side, value) {
  const current = { ...emptyToothRecord(), ...(iprData?.[toothNumber] || {}) };
  if (current.missing) return iprData;
  return {
    ...iprData,
    [toothNumber]: {
      ...current,
      [side]: parseFloat(value) || 0,
    },
  };
}
